import React from "react";
import { ds } from "../../DataSource";
import { FormValidationError, FormValidationFunction, Form, FormTextField, FormCheckbox, FormTextArea } from "@schneiderpp/utils-forms";
import { ParentStateDatasource, DataSourceStateIdle } from "@schneiderpp/utils-endpoint";
import { Endpoint } from "@schneiderpp/admin-endpoint";
import { BaseComponent } from "../../utils/BaseComponent";
import { RouteComponentProps, Link, withRouter } from "react-router-dom";
import { ROUTER_HOME } from "../home/Router";
import { OverlayProps, Overlay } from "@schneiderpp/utils-components";

interface EditFormFields {
    Name: string;
    Description: string;
    Link: string;
    IsActive: boolean;
    IsRequired: boolean;
}

interface EditState {
    providedAgreementId: number | undefined;
    fields: EditFormFields;
    fieldErrors: FormValidationError<EditFormFields>[];
    datasource: {
        Edit: ParentStateDatasource<typeof Endpoint.Agreement.PostAgreementEdit>;
        Details: ParentStateDatasource<typeof Endpoint.Agreement.GetAgreementDetails>;
    };
}

const editFormValidate: FormValidationFunction<EditFormFields> = async fields => {
    const errors: Array<FormValidationError<EditFormFields>> = [];
    if (fields.Name.length < 3) {
        errors.push({ fieldName: "Name", code: "NameTooShort" });
    }
    return errors;
};

class Edit extends BaseComponent<RouteComponentProps, EditState> {
    state: EditState = {
        providedAgreementId: undefined,
        fields: {
            Name: "",
            Description: "",
            Link: "",
            IsActive: false,
            IsRequired: false
        },
        fieldErrors: [],
        datasource: {
            Edit: DataSourceStateIdle,
            Details: DataSourceStateIdle
        }
    };

    private dsEdit = ds(Endpoint.Agreement.PostAgreementEdit, this, "Edit", () => this.context);

    private dsDetails = ds(Endpoint.Agreement.GetAgreementDetails, this, "Details", () => this.context);

    private form = new Form<EditFormFields>(this, editFormValidate, code => {
        switch (code) {
            case "MustBeNumber":
                return "Musi być numerem";
            case "MustBeHigher":
                return "Musi być większe niz 0";
            case "NameTooShort":
                return "Nazwa za krótka";
            default:
                return code;
        }
    });

    componentDidMount() {
        const urlParams = new URLSearchParams(window.location.search);
        const AgreementId = urlParams.get("AgreementId");
        if (!AgreementId) {
            return;
        }
        const AgreementIdInt = parseInt(AgreementId);
        if (!Number.isInteger(AgreementIdInt)) {
            return;
        }
        this.setState({ providedAgreementId: AgreementIdInt }, () => this.getDetails());
    }

    render() {
        if (!this.state.providedAgreementId) {
            return (
                <div className="page">
                    <div className="page__header">Nie odnaleziono AgreementId</div>
                </div>
            );
        }
        const dsDetailsData = this.dsDetails.dataSourceStorage;
        if (dsDetailsData.state === "completed") {
            return (
                <>
                    <div className="header">
                        <Link
                            to={{ pathname: ROUTER_HOME.Agreement.Details, search: `AgreementId=${this.state.providedAgreementId}` }}
                            className="button clear no-left-padding"
                        >
                            <span className="button__icon">arrow_back_ios</span> wróć do Detali zgody
                        </Link>
                    </div>
                    <div className="page">
                        <div className="page__header">Edytuj zgodę</div>
                        <div className="page-form">
                            <FormTextField config={this.form.getFieldConfig("Name")} label="Nazwa" />
                            <FormTextArea config={this.form.getFieldConfig("Description")} label="Opis" />
                            <FormTextField config={this.form.getFieldConfig("Link")} label="Link do dokumentu" />
                            <FormCheckbox config={this.form.getFieldConfig("IsActive")} label="Aktywna" checkboxLabel="Zgoda aktywna" />
                            <FormCheckbox config={this.form.getFieldConfig("IsRequired")} label="Wymagana" checkboxLabel="Zgoda wymagana" />

                            <button className="button align-self-end margin-top-10" onClick={() => this.submit()}>
                                zatwierdź
                            </button>
                        </div>
                        <Overlay {...this.overlayProps} />
                    </div>
                </>
            );
        }
        return (
            <div className="page">
                <Overlay {...this.overlayProps} />
            </div>
        );
    }

    get overlayProps(): OverlayProps {
        if (this.dsDetails.state === "pending" || this.dsDetails.state === "idle" || this.dsEdit.state === "pending") {
            return {
                show: true,
                title: "Ładowane..."
            };
        }
        if (this.dsDetails.state === "error") {
            return {
                show: true,
                title: "Coś poszło nie tak",
                children: (
                    <div className="overlay__children">
                        <button onClick={() => this.getDetails()}>Spróbuj ponownie</button>
                    </div>
                )
            };
        }
        if (this.dsEdit.state === "error") {
            return {
                show: true,
                title: "Coś poszło nie tak",
                children: (
                    <div className="overlay__children">
                        <button onClick={() => this.dsEdit.resetState()}>Spróbuj ponownie</button>
                    </div>
                )
            };
        }
        return {
            show: false
        };
    }

    private async getDetails() {
        if (!this.state.providedAgreementId) {
            return;
        }
        await this.dsDetails.request({
            params: { AgreementId: this.state.providedAgreementId }
        });
        const dsDetailsData = this.dsDetails.dataSourceStorage;
        if (dsDetailsData.state === "completed") {
            const response = dsDetailsData.response;
            this.setState(() => ({
                fields: {
                    Name: response.Name,
                    Description: response.Description || "",
                    Link: response.LinkToDocument || "",
                    IsActive: response.IsActive,
                    IsRequired: response.IsRequired
                }
            }));
        }
    }

    private async submit() {
        if (!this.state.providedAgreementId) {
            return;
        }
        const isValid = await this.form.validate();
        if (isValid) {
            await this.dsEdit.request({
                data: {
                    AgreementId: this.state.providedAgreementId,
                    Name: this.state.fields.Name,
                    Description: this.state.fields.Description,
                    LinkToDocument: this.state.fields.Link,
                    IsActive: this.state.fields.IsActive,
                    IsRequired: this.state.fields.IsRequired
                }
            });
            if (this.dsEdit.dataSourceStorage.state === "completed") {
                this.props.history.push({
                    pathname: ROUTER_HOME.Agreement.Details,
                    search: `AgreementId=${this.state.providedAgreementId}`
                });
            }
        }
    }
}

export default withRouter(Edit);
